import React, { useMemo } from 'react';
import cx from 'classnames';
import {
  Paper,
  Table as MuiTable,
  TableContainer,
  TableBody as MuiTableBody,
  TableHead,
  TableRow,
  TableCell as MuiTableCell,
  Skeleton,
} from '@mui/material';

import { ITableProps, IColumn } from './types';
import { TableTypes } from 'components/Table/constants';

import { ReactComponent as CheckIcon } from 'assets/check.icon.svg';
import { ReactComponent as CrossIcon } from 'assets/cross.icon.svg';

import useStyles from './styles';
import { getFormattedDate } from 'utils/dates';
import EllipsisTooltip from '../EllipsisTooltip';

const SimpleTable: React.FC<ITableProps> = ({ data, columns = [], loading = false, className }) => {
  const classes = useStyles();
  const columnsWithoutWidth = useMemo(() => {
    return columns.filter(col => !col.width).length;
  }, [columns]);

  const loadingRows = useMemo(() => {
    return new Array(5).fill(1);
  }, []);

  const renderCellContent = (value: any, column: IColumn, rowData: any) => {
    switch (column.type) {
      case TableTypes.boolean:
        return (
          <>
            {value ? (
              <CheckIcon className={classes.checkIcon} />
            ) : (
              <CrossIcon className={classes.crossIcon} />
            )}
          </>
        );
      case TableTypes.actions:
        return <>{value}</>;
      case TableTypes.custom:
        return <>{column?.accessor && column?.accessor(rowData)}</>;
      case TableTypes.date:
        return getFormattedDate(String(value));
      default:
        return <EllipsisTooltip text={value} />;
    }
  };

  const getCellJustification = (column: IColumn) => {
    if (column.type === TableTypes.bulk) {
      return 'center';
    }
    return column.labelAlignment ? column.labelAlignment : 'flex-start';
  };

  const getCellAlignment = (column: IColumn) => {
    if (column.type === TableTypes.bulk) {
      return 'center';
    }
    return 'center';
  };

  return (
    <TableContainer component={Paper} className={cx(classes.tableContainer, className)}>
      <MuiTable className={classes.table} stickyHeader>
        {/* Table Header */}
        <TableHead>
          <TableRow className={classes.tableRow}>
            {columns.map(column => (
              <MuiTableCell
                key={column.key}
                className={cx(classes.tableCell, classes.tableRowAligned)}
                style={{
                  maxWidth: !column.width
                    ? `calc(100% / ${columnsWithoutWidth})`
                    : `${column.width}px`,
                  justifyContent: getCellJustification(column),
                  alignItems: getCellAlignment(column),
                  maxHeight: '65px',
                  width: '100%',
                  minWidth: column.minWidth,
                }}
              >
                {column.name}
              </MuiTableCell>
            ))}
          </TableRow>
        </TableHead>

        {/* Table Body */}
        <MuiTableBody className={classes.tableBody}>
          {loading
            ? loadingRows.map((_, rowIndex) => (
                <TableRow key={rowIndex} className={cx(classes.tableRow, classes.tableRowAligned)}>
                  {columns.map(column => (
                    <MuiTableCell
                      key={column.key}
                      className={classes.tableCell}
                      style={{
                        maxWidth: column.width ? `${column.width}px` : '100%',
                      }}
                    >
                      <Skeleton className={classes.tableCell} />
                    </MuiTableCell>
                  ))}
                </TableRow>
              ))
            : data.map((row, rowIndex) => (
                <TableRow key={rowIndex} className={cx(classes.tableRow, classes.tableRowAligned)}>
                  {columns.map(column => {
                    const value = row[column.key];
                    return (
                      <MuiTableCell
                        key={column.key}
                        className={classes.tableCell}
                        style={{
                          maxWidth: column.width ? `${column.width}px` : '100%',
                          minWidth: column.minWidth ? `${column.minWidth}px` : '100%',
                        }}
                      >
                        {renderCellContent(value, column, row)}
                      </MuiTableCell>
                    );
                  })}
                </TableRow>
              ))}
        </MuiTableBody>
      </MuiTable>
    </TableContainer>
  );
};

export default SimpleTable;
